import axios, { AxiosResponse } from "axios";
import React from "react";
import { Alert, Container, Form } from "react-bootstrap";
import useXoltarNavigation from "../../hooks/useXoltarNav";
import BaseCardView from "../../Components/BaseCardView/BaseCardView";
import { Swiper as SwiperComponent, SwiperSlide } from "swiper/react";

import Swiper, { EffectCards, Pagination, Navigation } from "swiper";

import "swiper/css";
import "swiper/css/effect-cards";
import { useAppDispatch, useAppSelector } from "../../redux-stores/hooks/hooks";
import {
  selectRawQuestionnaireAnswer,
  setAnswer,
  setQuestionnaireInvalid,
  setQuestionnaireValid,
} from "../../redux-stores/reducers/QuestionerState";
import { selectQuestionFile } from "../../redux-stores/reducers/PageState";
import { useWebValidation } from "../../hooks/websiteValidatioHook";
import { useCoreAvailability } from "../../hooks/validateCores";
import { setConversation } from "../../redux-stores/reducers/ConversationState";

const _QuestionsTypes = ["text", "dropdown", "slider"] as const;

interface SingleQuestion {
  question: string;
  type: typeof _QuestionsTypes[number];
  name: string;
  data: string[];
  extra: string;
  placeholder: string;
  required?: boolean;
  value: string;
  min?: number;
  max?: number;
  steps?: number;
}

interface QuestionCardFormat {
  title: string;
  questions: {
    [index: string]: SingleQuestion;
  };
}

interface QuestionProps {
  key_name: string;
  value?: string;
  question: SingleQuestion;
  onChange?: (values: AnswerType) => void;
}

function LinerQuestion(props: QuestionProps) {
  const { question, key_name } = props;
  const [value, setValue] = React.useState<string>(props.value ?? "");
  return (
    <>
      <Form.Label>{question.question}</Form.Label>
      <Form.Control
        required
        value={value}
        placeholder={question.placeholder}
        onChange={(e) => {
          setValue(e.target.value);
          props.onChange &&
            props.onChange({
              key: key_name,
              value: e.target.value,
            });
        }}
      />
    </>
  );
}
function DropDownQuestion(props: QuestionProps) {
  const { question, key_name } = props;
  const [value, setValue] = React.useState<string | undefined>(props.value);
  return (
    <>
      <Form.Label>{question.question}</Form.Label>
      <Form.Select
        value={value}
        onChange={(e) => {
          if (e.target.value !== "-1") setValue(e.target.value);
          props.onChange &&
            props.onChange({
              key: key_name,
              value: e.target.value !== "-1" ? e.target.value : undefined,
            });
        }}
      >
        <option value={"-1"}>{question.placeholder}</option>
        {question.data.map((data, i) => (
          <option key={`${key_name}-${i}`} value={data}>
            {data}
          </option>
        ))}
      </Form.Select>
    </>
  );
}
function RangeQuestion(props: QuestionProps) {
  const { question, key_name } = props;
  const { min = 0, max = 100, steps = undefined } = question;
  const [value, setValue] = React.useState<string>(props.value ?? "");

  return (
    <>
      <Form.Label>
        {question.question} {value}
      </Form.Label>
      <Form.Range
        min={min}
        max={max}
        step={steps}
        onChange={(e) => {
          console.debug("Range", e.target.value);

          setValue(e.target.value);
          props.onChange &&
            props.onChange({
              key: key_name,
              value: e.target.value,
            });
        }}
      />
    </>
  );
}
function Question(props: QuestionProps) {
  const { question } = props;

  return (
    <Form>
      <Form.Group as={"div"} className="mb-3">
        {question.type === "text" && <LinerQuestion {...props} />}
        {question.type === "dropdown" && <DropDownQuestion {...props} />}
        {question.type === "slider" && <RangeQuestion {...props} />}
        <Form.Text className="text-muted">{question.extra}</Form.Text>
      </Form.Group>
    </Form>
  );
}

type AnswerType = { key: string; value?: string };

function InputErrorAlert(props: {
  cardQuestions: QuestionCardFormat;
  answers: any;
  onClose?: Function;
}) {
  const { cardQuestions, answers, onClose } = props;
  const keys = Object.keys(cardQuestions.questions);
  const wrongKeys = [];
  for (let index = 0; index < keys.length; index++) {
    const element = cardQuestions.questions[keys[index]];
    const v = element;
    if (!answers[keys[index]] && v.required) wrongKeys.push(element.name);
  }
  if (wrongKeys.length > 0) {
    return (
      <Alert variant="danger" dismissible onClose={() => onClose && onClose()}>
        {wrongKeys.map((k) => (
          <div key={`alert-${k}`}>
            Missing {k}
            <br />
          </div>
        ))}
      </Alert>
    );
  } else {
    return null;
  }
}

function Questions() {
  useWebValidation();
  useCoreAvailability();
  const savedAnswers = useAppSelector(selectRawQuestionnaireAnswer);
  const questionerFileName = useAppSelector(selectQuestionFile);

  const [error, setError] = React.useState<boolean>(false);
  const [questions, setQuestions] = React.useState<QuestionCardFormat[]>([]);
  const swiper = React.useRef<Swiper>();
  const navigate = useXoltarNavigation();
  const dispatch = useAppDispatch();
  React.useEffect(() => {
    console.debug("questionerFileName", questionerFileName);
    axios
      .get<any, AxiosResponse<QuestionCardFormat[]>>(
        `/static/configs/${questionerFileName}`
      )
      .then((res) => {
        console.log(res.data);
        setQuestions(res.data);
      })
      .finally(() => {});
  }, [questionerFileName]);
  React.useEffect(() => {
    const clearError = () => {
      setError(false);
    };
    swiper.current?.on("slideChange", clearError);
    return () => swiper.current?.off("slideChange", clearError);
  }, []);
  const answers = React.useMemo(() => {
    const response: any = {};
    Object.entries(savedAnswers).forEach(([key, value]) => {
      response[key.replace("Questionnaire-", "")] = value;
    });
    return { ...response };
  }, [savedAnswers]);
  const addAnswer = (key: string, value?: string) => {
    if (value) {
      answers[key] = value;
      if (key === "conversation") {
        dispatch(setConversation(value));
      }
    } else {
      delete answers[key];
    }
    console.debug("answers", answers);
  };
  const onPrev = React.useCallback(
    (index: number) => {
      if (index === 0) {
        navigate("Policy");
      } else {
        swiper.current?.slidePrev();
      }
    },
    [navigate]
  );
  const onNext = React.useCallback(
    (index: number, cardQuestions: QuestionCardFormat) => {
      const cardQuestionsKeys = Object.keys(cardQuestions.questions);
      for (let i = 0; i < cardQuestionsKeys.length; i++) {
        const key = cardQuestionsKeys[i];
        const element = cardQuestions.questions[key];
        if (element.required && !answers[key]) {
          setError(true);
          return;
        }
        setError(false);
      }
      Object.entries(answers).forEach(([key, value]) => {
        if (key && typeof value === "string")
          dispatch(setAnswer({ key, value }));
      });
      if (index === questions.length - 1) {
        dispatch(setQuestionnaireValid());
        setTimeout(() => navigate("HardwareTest"), 0);
      } else {
        dispatch(setQuestionnaireInvalid());
        swiper.current?.slideNext();
      }
    },
    [questions, answers, dispatch, navigate]
  );

  return (
    <>
      {questions && questions.length >= 1 ? (
        <Container
          fluid
          className="question-container my-auto"
          style={{ height: "ft-content" }}
        >
          <SwiperComponent // install Swiper modules
            style={{
              width: "100%",
              height: "100%",
            }}
            centeredSlidesBounds={true}
            centeredSlides={true}
            modules={[EffectCards, Pagination, Navigation]}
            touchRatio={0}
            pagination={{
              clickable: false,
              type: "custom",
            }}
            className="mySwiper"
            onSwiper={(s) => {
              swiper.current = s;
            }}
          >
            {questions.map((v, j) => {
              return (
                <SwiperSlide
                  className="Slider"
                  key={`Slider-question-${j}`}
                  style={{
                    height: "100%",
                  }}
                >
                  <BaseCardView
                    title={v.title}
                    cardStyle={{ width: "100%", overflow: "auto" }}
                    headerStyle={{}}
                    bodyStyle={{
                      height: "70%",
                      overflow: "auto",
                    }}
                    footerStyle={{}}
                    onPrevious={() => {
                      onPrev(j);
                    }}
                    onNext={() => {
                      onNext(j, v);
                    }}
                    bodyComponent={() => {
                      return (
                        <Container fluid key={`container-Slider-${j}`}>
                          {Object.keys(v.questions).map((key) => {
                            return (
                              <Question
                                onChange={(e) => addAnswer(e.key, e.value)}
                                key={`question-s-${j}-${Math.random()}`}
                                question={v.questions[key]}
                                value={answers[key]}
                                key_name={key}
                              />
                            );
                          })}
                          {error ? (
                            <InputErrorAlert
                              cardQuestions={v}
                              answers={answers}
                              onClose={() => setError(false)}
                            />
                          ) : null}
                        </Container>
                      );
                    }}
                  />
                </SwiperSlide>
              );
            })}
          </SwiperComponent>
        </Container>
      ) : (
        <Container>Loading</Container>
      )}
    </>
  );
}

export default Questions;
